<template>
  <div class="cost-price-chart">
    <div class="cost-price-head">
      <h3>Custo Médio e Preço</h3>
      <div class="cost-price-controls">
        <label class="cost-price-check">
          <input type="checkbox" v-model="includeSpot" />
          Spot
        </label>
        <label class="cost-price-check">
          <input type="checkbox" v-model="includeFutures" />
          Futuros
        </label>
        <strong class="cost-price-last" style="color: #26a69a">Long {{ lastLongLabel }}</strong>
        <strong class="cost-price-last" style="color: #a78bfa">Short {{ lastShortLabel }}</strong>
        <strong class="cost-price-last" style="color: #38bdf8">Preço {{ lastPriceLabel }}</strong>
      </div>
    </div>
    <p v-if="!hasSelection" class="cost-price-empty">Selecione spot, futuros ou os dois.</p>
    <div v-else-if="points.length" class="cost-price-plot">
      <apexchart
        :key="chartKey"
        type="line"
        height="100%"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
    <p v-else class="cost-price-empty">Nenhum evento do período com snapshot.</p>
  </div>
</template>

<script>
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { pmpLevelsFromSnapshot } from '@/utils/computeEdgesSeries.js';

function formatPrice(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return '$' + Math.round(n).toLocaleString('en-US');
}

function formatQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(4);
}

function formatSignedPrice(value) {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';
  return sign + '$' + Math.abs(Math.round(n)).toLocaleString('en-US');
}

function pctChange(current, previous, { lowerIsBetter = false } = {}) {
  const curr = Number(current);
  const prev = Number(previous);
  if (!Number.isFinite(curr) || curr <= 0 || !Number.isFinite(prev) || prev <= 0) {
    return { text: '—', color: '#e5e7eb' };
  }
  const pct = ((curr - prev) / prev) * 100;
  const sign = pct > 0 ? '+' : '';
  const improved = lowerIsBetter ? pct < 0 : pct > 0;
  const worsened = lowerIsBetter ? pct > 0 : pct < 0;
  const color = improved ? '#4ade80' : worsened ? '#f87171' : '#e5e7eb';
  return { text: sign + pct.toFixed(2) + '%', color };
}

function plotPrice(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Number(n.toFixed(2)) : null;
}

export default {
  name: 'EvolCostPriceChart',
  props: {
    timeSeries: { type: Array, required: true },
    aggLabels: { type: Array, required: true },
  },
  data() {
    return {
      includeSpot: true,
      includeFutures: true,
    };
  },
  computed: {
    hasSelection() {
      return this.includeSpot || this.includeFutures;
    },
    points() {
      if (!this.hasSelection) return [];
      return (this.timeSeries || []).map(({ index, m }) => ({
        index,
        price: plotPrice(m?.currentPrice),
        ...pmpLevelsFromSnapshot(m?.manualsSnapshot, {
          spot: this.includeSpot,
          futures: this.includeFutures,
        }),
      }));
    },
    lastPoint() {
      return this.points[this.points.length - 1] || null;
    },
    lastLongLabel() {
      return formatPrice(this.lastPoint?.pmpLong);
    },
    lastShortLabel() {
      return formatPrice(this.lastPoint?.pmpShort);
    },
    lastPriceLabel() {
      return formatPrice(this.lastPoint?.price);
    },
    chartKey() {
      const last = this.lastPoint;
      return `cost-price-${this.includeSpot}-${this.includeFutures}-${this.points.length}-${last?.pmpLong ?? 0}-${last?.pmpShort ?? 0}-${last?.price ?? 0}`;
    },
    chartSeries() {
      return [
        {
          name: 'Custo Long',
          data: this.points.map((point) => [point.index, plotPrice(point.pmpLong)]),
        },
        {
          name: 'Custo Short',
          data: this.points.map((point) => [point.index, plotPrice(point.pmpShort)]),
        },
        {
          name: 'Preço',
          data: this.points.map((point) => [point.index, point.price]),
        },
      ];
    },
    chartOptions() {
      const self = this;
      return {
        ...defaultChartOptions,
        colors: ['#26a69a', '#a78bfa', '#38bdf8'],
        legend: { ...defaultChartOptions.legend },
        stroke: { curve: 'straight', width: [2, 2, 2.5], dashArray: [0, 0, 6] },
        markers: { size: 0, hover: { size: 4 }, strokeWidth: 0 },
        grid: {
          ...defaultChartOptions.grid,
          padding: { right: 24, left: 12, top: 8 },
        },
        xaxis: { type: 'numeric', labels: { formatter: this.xAxisFormatter } },
        yaxis: {
          labels: { formatter: (val) => formatPrice(val) },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom({ dataPointIndex }) {
            return self.tooltipHtml(dataPointIndex);
          },
        },
      };
    },
  },
  methods: {
    xAxisFormatter(val) {
      return this.aggLabels[Math.floor(val)] || '';
    },
    tooltipHtml(dataPointIndex) {
      const point = this.points[dataPointIndex];
      if (!point) return '';
      const previous = dataPointIndex > 0 ? this.points[dataPointIndex - 1] : null;
      const label = this.aggLabels[dataPointIndex] || '';
      const pricePct = pctChange(point.price, previous?.price);
      const longPct = pctChange(point.pmpLong, previous?.pmpLong, { lowerIsBetter: true });
      const shortPct = pctChange(point.pmpShort, previous?.pmpShort);
      const longGap = point.price != null && point.pmpLong != null ? point.price - point.pmpLong : null;
      const shortGap = point.price != null && point.pmpShort != null ? point.pmpShort - point.price : null;
      const longColor = longGap > 0 ? '#4ade80' : longGap < 0 ? '#f87171' : '#e5e7eb';
      const shortColor = shortGap > 0 ? '#4ade80' : shortGap < 0 ? '#f87171' : '#e5e7eb';
      return `
        <div style="background:#1e1e1e;color:#fff;padding:10px 14px;border:1px solid #444;border-radius:6px;font-size:12px;min-width:240px;">
          <div style="font-weight:600;margin-bottom:8px;border-bottom:1px solid #444;padding-bottom:4px;color:#38bdf8;">${label}</div>
          <div style="line-height:1.7;">
            <div style="color:#38bdf8;">Preço: <b>${formatPrice(point.price)}</b> <b style="color:${pricePct.color}">${pricePct.text}</b></div>
            <div style="color:#26a69a;">Custo Long: <b>${formatPrice(point.pmpLong)}</b> <b style="color:${longPct.color}">${longPct.text}</b> (${formatQty(point.longQty)})</div>
            <div style="color:#a78bfa;">Custo Short: <b>${formatPrice(point.pmpShort)}</b> <b style="color:${shortPct.color}">${shortPct.text}</b> (${formatQty(point.shortQty)})</div>
            <div style="margin-top:6px;padding-top:6px;border-top:1px solid #444;">
              <div>Preço − Long: <b style="color:${longColor}">${formatSignedPrice(longGap)}</b></div>
              <div>Short − Preço: <b style="color:${shortColor}">${formatSignedPrice(shortGap)}</b></div>
            </div>
          </div>
        </div>
      `;
    },
  },
};
</script>

<style scoped>
.cost-price-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background-color: var(--table-header-bgcolor);
  border-radius: 10px;
  padding: 16px 16px 8px;
}

.cost-price-head h3 {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #f1f1f1;
}

.cost-price-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 8px;
}

.cost-price-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-color, #f1f1f1);
  cursor: pointer;
}

.cost-price-check input[type="checkbox"] {
  margin: 0;
}

.cost-price-last {
  font-size: 15px;
  font-weight: 700;
}

.cost-price-plot {
  height: calc(100% - 78px);
}

.cost-price-empty {
  margin: 0;
  opacity: 0.7;
}
</style>
